import { ArweaveWalletKit } from "@arweave-wallet-kit/react"
import WanderStrategy from "@arweave-wallet-kit/wander-strategy"
import { Container, CssBaseline, Link, Stack, Typography } from "@mui/material"
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles"

import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import AoSyncStrategy from "@vela-ventures/aosync-strategy"
import { AnimatePresence } from "framer-motion"
import { HashRouter, Navigate, Route, Routes } from "react-router-dom"

import ConfigProvider from "./components/ConfigProvider"
import Disclaimer from "./components/Disclaimer"
import { MySnackbarProvider } from "./components/MySnackbarProvider"
import Nav from "./components/Nav"
import RouteAnimation from "./components/RouteAnimation"

import { AnimTestPage } from "./components/create-agent-anim/AnimTestPage"
import { Footer } from "./components/layout/Footer"
import { ConfigContext } from "./contexts/config"

import "./App.css"
import { EthereumProvider } from "./providers/EthereumProvider"
import Add from "./routes/add"
import Config from "./routes/config"
import MintPage from "./routes/mint"
import MyAccount from "./routes/my-account"
import PointsFarmingPage from "./routes/points"
import Pools from "./routes/pools"
import Remove from "./routes/remove"
import SimpleRemove from "./routes/simple-remove"
import SwapWithRouter from "./routes/swap-base"
import { theme } from "./theme"
import { parseGateway } from "./utils/format"
import { MaintenancePage } from "./components/Maintenance"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Check for specific error types to determine if we should retry
        if (error instanceof Error) {
          const err = error as any

          // Explicit check for 429 rate limiting errors
          const is429Error =
            err.status === 429 ||
            err.statusCode === 429 ||
            error.message.includes("429") ||
            error.message.includes("Too Many Requests")

          if (is429Error) {
            console.log(`Retrying query after 429 rate limit error (attempt ${failureCount})`)
            // Be more persistent with rate limit retries
            return failureCount < 10 // Try up to 10 times for 429s
          }

          // Check for other network failures
          const isNetworkError =
            error.message.includes("Failed to fetch") ||
            error.message.includes("Network request failed") ||
            err.status === 503 ||
            err.statusCode === 503

          if (isNetworkError) {
            console.log(`Retrying query after network error (attempt ${failureCount})`)
            return failureCount < 8 // Try up to 8 times for network issues
          }
        }

        // Default React Query retry logic for other errors
        return failureCount < 3
      },
      retryDelay: (attemptIndex) => {
        // Even faster for 429 errors, as they're expected to clear quickly
        const baseDelay = 300 // Start at 300ms
        const delay = Math.min(baseDelay * 1.3 ** attemptIndex, 3000) // Max 3 seconds

        // Small jitter to prevent all clients retrying simultaneously
        const jitter = delay * 0.1 * Math.random()

        console.log(
          `Global retry delay: ${Math.round(delay + jitter)}ms (attempt ${attemptIndex + 1})`,
        )
        return delay + jitter
      },
    },
  },
})

export default function App() {
  return (
    <EthereumProvider>
      <CssVarsProvider theme={theme} defaultMode="system">
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <ConfigProvider>
            <ConfigContext.Consumer>
              {([config]) => (
                <ArweaveWalletKit
                  theme={{
                    displayTheme: "dark",
                    accent: {
                      r: 128,
                      g: 0,
                      b: 255,
                    },
                    titleHighlight: {
                      r: 128,
                      g: 0,
                      b: 255,
                    },
                    radius: "none",
                  }}
                  config={{
                    appInfo: {
                      name: "Botega",
                      logo: `${window.location.origin}/bark.png`,
                    },
                    permissions: [
                      "ACCESS_ADDRESS",
                      "ACCESS_ALL_ADDRESSES",
                      "SIGN_TRANSACTION",
                      "ACCESS_TOKENS" as any, //TODO needs to be fixed upstream
                    ],
                    gatewayConfig: parseGateway(config.GATEWAY_URL),
                    strategies: [new WanderStrategy(), new AoSyncStrategy()],
                  }}
                >
                  <HashRouter>
                    <Disclaimer defaultOpen={true}>
                      <Stack
                        alignItems="center"
                        sx={{
                          background: "var(--mui-palette-buttonBgSecondary)",
                          paddingY: 0.5,
                          paddingX: 1.5,
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="caption">
                          Botega fairlaunched the offical Botega Token <b>$BOTG</b>, read more{" "}
                          <Link
                            href="https://docs.autonomous.finance/products/platforms/botega/botega-tokenomics"
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: "black", textDecoration: "underline" }}
                          >
                            here
                          </Link>
                        </Typography>
                      </Stack>
                      <Nav />
                      <Container
                        maxWidth="xl"
                        sx={{ minHeight: "calc(100vh - 176px)", paddingY: "5rem" }}
                      >
                        <AnimatePresence>
                          <RouteAnimation>
                            {/* <Routes>
                              <Route path="/" element={<Navigate to="/swap" replace />} />
                              <Route path="/swap" element={<SwapWithRouter />} />
                              <Route path="/pools" element={<Pools />} />
                              <Route path="/add" element={<Add />} />
                              <Route path="/remove" element={<Remove />} />
                              <Route path="/config" element={<Config />} />
                              <Route path="/my-account" element={<MyAccount />} />
                              <Route path="/anim" element={<AnimTestPage />} />
                              <Route path="/mint" element={<MintPage />} />
                              <Route path="/rewards" element={<PointsFarmingPage />} />
                            </Routes> */}
                            <Routes>
                              <Route path="/" element={<MaintenancePage />} />
                              <Route path="/swap" element={<Navigate to="/" replace />} />
                              <Route path="/pools" element={<Navigate to="/" replace />} />
                              <Route path="/add" element={<Navigate to="/" replace />} />
                              <Route path="/remove" element={<Navigate to="/" replace />} />
                              <Route path="/config" element={<Navigate to="/" replace />} />
                              <Route path="/my-account" element={<Navigate to="/" replace />} />
                              <Route path="/anim" element={<Navigate to="/" replace />} />
                              <Route path="/mint" element={<Navigate to="/" replace />} />
                              <Route path="/rewards" element={<Navigate to="/" replace />} />
                              <Route path="/simple-remove" element={<SimpleRemove />} />
                            </Routes>
                          </RouteAnimation>
                        </AnimatePresence>
                      </Container>
                      <Footer />
                      <MySnackbarProvider />
                    </Disclaimer>
                  </HashRouter>
                </ArweaveWalletKit>
              )}
            </ConfigContext.Consumer>
          </ConfigProvider>
        </QueryClientProvider>
      </CssVarsProvider>
    </EthereumProvider>
  )
}
